import { flare } from '@flareapp/js';

import { trackAnalyticsEvent } from '@/utils/analytics/track';

/**
 * Utility for reporting caught errors to Flare while maintaining console output.
 * Also emits an analytics event so errors can be correlated with user/game context.
 */
export class ErrorReporter {
  static report(
    error: Error | string | unknown,
    context: {
      component?: string;
      action?: string;
      gameId?: string | number;
      userId?: string;
      additionalData?: Record<string, any>;
    } = {},
    level: 'error' | 'warning' | 'info' = 'error'
  ): void {
    // Always log to console for development
    const contextStr =
      Object.keys(context).length > 0 ? JSON.stringify(context, null, 2) : '';

    if (level === 'error') {
      console.error(
        '🚨 Caught Error:',
        error,
        contextStr ? '\nContext:' : '',
        contextStr
      );
    } else if (level === 'warning') {
      console.warn(
        '⚠️ Caught Warning:',
        error,
        contextStr ? '\nContext:' : '',
        contextStr
      );
    } else {
      console.log(
        'ℹ️ Caught Info:',
        error,
        contextStr ? '\nContext:' : '',
        contextStr
      );
    }

    const safeAdditionalData = (() => {
      if (!context.additionalData) return undefined;

      try {
        return JSON.parse(JSON.stringify(context.additionalData));
      } catch {
        return { unserializableAdditionalData: true };
      }
    })();

    // Emit analytics error event (even if Flare is disabled)
    try {
      const normalizedError = (() => {
        if (error instanceof Error) return error;
        if (typeof error === 'string') {
          const e = new Error(error);
          e.name = 'CaughtError';
          return e;
        }

        const e = new Error(`Unknown error type: ${String(error)}`);
        e.name = 'UnknownError';
        return e;
      })();

      trackAnalyticsEvent({
        type: level === 'error' ? 'caught_error' : `caught_${level}`,
        target: context.component || 'app',
        gameId: context.gameId != null ? String(context.gameId) : undefined,
        error: {
          message: normalizedError.message,
          stack: normalizedError.stack,
        },
        metadata: {
          level,
          component: context.component,
          action: context.action,
          userId: context.userId,
          additionalData: safeAdditionalData,
        },
      });
    } catch (analyticsError) {
      console.warn(
        '[ErrorReporter] Failed to emit analytics error event:',
        analyticsError
      );
    }

    // Only send to Flare in production or when explicitly enabled
    if (
      import.meta.env.MODE === 'production' ||
      import.meta.env.VITE_ENABLE_FLARE_REPORTING === 'true'
    ) {
      try {
        const normalizedError = (() => {
          if (error instanceof Error) return error;
          if (typeof error === 'string') {
            const e = new Error(error);
            e.name = 'CaughtError';
            return e;
          }

          const e = new Error(`Unknown error type: ${String(error)}`);
          e.name = 'UnknownError';
          return e;
        })();

        const flareContext: Record<string, any> = {
          level,
          timestamp: new Date().toISOString(),
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          ...context,
          additionalData: safeAdditionalData,
        };

        void flare.report(normalizedError, flareContext).catch((flareError) => {
          console.error('Failed to report error to Flare:', flareError);
        });
      } catch (flareError) {
        console.error('Failed to report error to Flare:', flareError);
      }
    }
  }

  /**
   * Convenience method for reporting errors with game context
   */
  static reportGameError(
    error: Error | string | unknown,
    gameContext: {
      gameId: string | number;
      component: string;
      action: string;
      round?: number;
      level?: number;
      userId?: string;
      additionalData?: Record<string, any>;
    }
  ): void {
    this.report(error, gameContext, 'error');
  }

  /**
   * Convenience method for reporting audio-related errors
   */
  static reportAudioError(
    error: Error | string | unknown,
    audioContext: {
      component: string;
      audioFile?: string;
      action: string;
      additionalData?: Record<string, any>;
    }
  ): void {
    this.report(
      error,
      {
        ...audioContext,
        additionalData: { ...audioContext.additionalData, category: 'audio' },
      },
      'error'
    );
  }

  /**
   * Convenience method for reporting API/network errors
   */
  static reportNetworkError(
    error: Error | string | unknown,
    networkContext: {
      endpoint?: string;
      method?: string;
      statusCode?: number;
      component: string;
      additionalData?: Record<string, any>;
    }
  ): void {
    this.report(
      error,
      {
        ...networkContext,
        additionalData: {
          ...networkContext.additionalData,
          category: 'network',
        },
      },
      'error'
    );
  }
}
